import { centsToAmountString } from "@capture/shared";
import { formatMoneyCents } from "../../remittance/lib/money";

interface CheckAmountCandidate {
  cents: number;
  confidence: number;
}

interface CheckAmountAidProps {
  candidates: {
    car: CheckAmountCandidate | null;
    lar: CheckAmountCandidate | null;
  };
  /** Fill the field input with the keyable amount string (never auto-accept). */
  onFill: (value: string) => void;
}

/**
 * CAR/LAR check-amount keying aid — the panel under the active amount field
 * when the courtesy (numeric) and legal (written) readings did NOT cross-
 * confirm (agreement:"none", so no ocrValue pre-fill was applied). It offers
 * each engine's reading as a click-to-FILL button: filling stages the value in
 * the input for the operator to confirm — it never auto-accepts. Native
 * <button>s make it keyboard-accessible (Tab to reach, Enter/Space to fill).
 * On a confirming agreement the mapper yields no candidates and this never
 * mounts (the pre-fill already covers the field).
 */
export function CheckAmountAid({ candidates, onFill }: CheckAmountAidProps) {
  const rows: Array<{ key: "car" | "lar"; kind: string; cand: CheckAmountCandidate }> = [];
  if (candidates.car) rows.push({ key: "car", kind: "numeric", cand: candidates.car });
  if (candidates.lar) rows.push({ key: "lar", kind: "written", cand: candidates.lar });
  if (rows.length === 0) return null;

  return (
    <div
      className="check-amount-aid"
      role="group"
      aria-label="Amount readings disagreed — click a reading to fill, then confirm"
    >
      <span className="check-amount-aid-title" aria-hidden="true">
        Machine read (unconfirmed):
      </span>
      {rows.map((row) => (
        <button
          key={row.key}
          type="button"
          className="check-amount-candidate"
          data-side={row.key}
          onClick={() => onFill(centsToAmountString(row.cand.cents))}
        >
          <span className="check-amount-value">{formatMoneyCents(row.cand.cents)}</span>
          <span className="check-amount-kind">{row.kind}</span>
          <span className="check-amount-conf">{Math.round(row.cand.confidence * 100)}%</span>
        </button>
      ))}

      <style>{`
        .check-amount-aid {
          margin-top: var(--space-1);
          padding: var(--space-1) var(--space-2);
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border);
          border-left: 3px solid var(--color-primary);
          border-radius: var(--radius-sm);
          font-size: var(--text-xs);
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: var(--space-2);
        }
        .check-amount-aid-title {
          flex-shrink: 0;
          font-size: 0.6rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--color-text-muted);
        }
        .check-amount-candidate {
          display: inline-flex;
          align-items: baseline;
          gap: var(--space-2);
          padding: 2px var(--space-2);
          background: var(--color-bg-tertiary);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          color: var(--color-text-primary);
          cursor: pointer;
          font: inherit;
        }
        .check-amount-candidate:hover {
          border-color: var(--color-primary);
          background: var(--overlay-medium);
        }
        .check-amount-candidate:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(252, 148, 48, 0.35);
        }
        .check-amount-value {
          font-family: "SF Mono", "Fira Code", Consolas, monospace;
          font-weight: 700;
          color: var(--color-primary);
        }
        .check-amount-kind {
          font-size: 0.6rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--color-text-muted);
        }
        .check-amount-conf {
          font-size: 0.6rem;
          color: var(--color-text-muted);
        }
      `}</style>
    </div>
  );
}
